import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSnackbar } from 'notistack';
import {
  dispositivoRedeService,
  DispositivoRede,
  DispositivoRedeFormData,
  DispositivoRedeTipo,
  DispositivoRedeContratoServico,
} from '../../../services/dispositivoRedeService';
import { StatusFilter, REDE_MONITOR_POLL_MS } from './monitoramentoRedeUtils';

const emptyForm: DispositivoRedeFormData = {
  nome: '',
  tipo: 'switch',
  modo_monitoramento: 'ping_ip',
  monitorar_ate: '',
  endereco_ip: '',
  contrato_servico_id: null,
  ativo: true,
};

export function useDispositivosRede(enabled: boolean) {
  const { enqueueSnackbar } = useSnackbar();
  const [dispositivos, setDispositivos] = useState<DispositivoRede[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtroTipo, setFiltroTipo] = useState<DispositivoRedeTipo | ''>('');
  const [activeStatusFilter, setActiveStatusFilter] = useState<StatusFilter>('todos');
  const [formOpen, setFormOpen] = useState(false);
  const [formData, setFormData] = useState<DispositivoRedeFormData>(emptyForm);
  const [editing, setEditing] = useState<DispositivoRede | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<DispositivoRede | null>(null);
  const [servicoOptions, setServicoOptions] = useState<DispositivoRedeContratoServico[]>([]);
  const [servicoSearchLoading, setServicoSearchLoading] = useState(false);
  const [selectedServico, setSelectedServico] = useState<DispositivoRedeContratoServico | null>(null);
  const pollingRef = useRef(false);

  const loadDispositivos = useCallback(async (silent = false) => {

    if (silent && pollingRef.current) return;
    if (silent) pollingRef.current = true;

    try {
      if (!silent) setLoading(true);
      const data = await dispositivoRedeService.list({
        search: search || undefined,
        tipo: filtroTipo || undefined,
      });
      setDispositivos(data);
    } catch {
      if (!silent) {
        enqueueSnackbar('Erro ao carregar dispositivos', { variant: 'error' });
        setDispositivos([]);
      }
    } finally {
      if (!silent) setLoading(false);
      if (silent) pollingRef.current = false;
    }
  }, [search, filtroTipo, enqueueSnackbar]);

  useEffect(() => {
    if (!enabled) return undefined;

    void loadDispositivos();

    const intervalId = window.setInterval(() => {
      void loadDispositivos(true);
    }, REDE_MONITOR_POLL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [enabled, loadDispositivos]);

  const stats = useMemo(() => {
    const online = dispositivos.filter((d) => d.monitor_status === 'online').length;
    const offline = dispositivos.filter((d) => d.monitor_status === 'offline').length;
    const unknown = dispositivos.filter((d) => d.monitor_status === 'unknown').length;
    return { online, offline, unknown, total: dispositivos.length };
  }, [dispositivos]);

  const filteredDispositivos = useMemo(() => {
    if (activeStatusFilter === 'todos') return dispositivos;
    return dispositivos.filter((d) => d.monitor_status === activeStatusFilter);
  }, [dispositivos, activeStatusFilter]);

  const handleFilterChange = useCallback((filter: StatusFilter) => {
    setActiveStatusFilter(filter);
  }, []);

  const handleOpenCreate = useCallback(() => {
    setEditing(null);
    setFormData(emptyForm);
    setSelectedServico(null);
    setFormOpen(true);
  }, []);

  const handleOpenEdit = useCallback((dispositivo: DispositivoRede) => {
    setEditing(dispositivo);
    setFormData({
      nome: dispositivo.nome,
      tipo: dispositivo.tipo,
      modo_monitoramento: dispositivo.modo_monitoramento || 'ping_ip',
      monitorar_ate: dispositivo.monitorar_ate ? dispositivo.monitorar_ate.substring(0, 10) : '',
      endereco_ip: dispositivo.endereco_ip || '',
      contrato_servico_id: dispositivo.contrato_servico_id ?? null,
      ativo: dispositivo.ativo,
    });
    setSelectedServico(dispositivo.contrato_servico ?? null);
    setFormOpen(true);
  }, []);

  const handleSave = useCallback(async () => {
    if (!formData.nome.trim()) {
      enqueueSnackbar('Informe o nome do dispositivo', { variant: 'warning' });
      return;
    }

    if (formData.tipo === 'cliente' && !formData.contrato_servico_id) {
      enqueueSnackbar('Selecione o serviço do contrato', { variant: 'warning' });
      return;
    }

    if (formData.tipo !== 'cliente' && !formData.endereco_ip?.trim()) {
      enqueueSnackbar('Informe o endereço IP', { variant: 'warning' });
      return;
    }

    try {
      setSaving(true);
      const payload: DispositivoRedeFormData = {
        nome: formData.nome.trim(),
        tipo: formData.tipo,
        modo_monitoramento: formData.tipo === 'cliente' ? (formData.modo_monitoramento || 'ping_ip') : undefined,
        monitorar_ate: formData.monitorar_ate?.trim() || null,
        ativo: formData.ativo,
        endereco_ip: formData.tipo !== 'cliente' ? formData.endereco_ip?.trim() : undefined,
        contrato_servico_id: formData.tipo === 'cliente' ? formData.contrato_servico_id : null,
      };

      if (editing) {
        await dispositivoRedeService.update(editing.id, payload);
        enqueueSnackbar('Dispositivo atualizado', { variant: 'success' });
      } else {
        await dispositivoRedeService.create(payload);
        enqueueSnackbar('Dispositivo cadastrado', { variant: 'success' });
      }

      setFormOpen(false);
      await loadDispositivos();
    } catch {
      enqueueSnackbar('Erro ao salvar dispositivo', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  }, [formData, editing, enqueueSnackbar, loadDispositivos]);

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      await dispositivoRedeService.remove(deleteTarget.id);
      enqueueSnackbar('Dispositivo excluído', { variant: 'success' });
      setDeleteTarget(null);
      await loadDispositivos();
    } catch {
      enqueueSnackbar('Erro ao excluir dispositivo', { variant: 'error' });
    }
  }, [deleteTarget, enqueueSnackbar, loadDispositivos]);

  const handleServicoSearch = useCallback(async (value: string) => {
    if (value.length < 2) {
      setServicoOptions([]);
      return;
    }
    try {
      setServicoSearchLoading(true);
      const results = await dispositivoRedeService.searchServicos(value);
      setServicoOptions(results);
    } catch {
      setServicoOptions([]);
    } finally {
      setServicoSearchLoading(false);
    }
  }, []);

  const getServicoLabel = useCallback((servico: DispositivoRedeContratoServico) => {
    const cliente = servico.contrato?.cliente?.nome ?? 'Cliente';
    const contrato = servico.contrato?.titulo ?? `#${servico.contrato_id}`;
    const pppoe = servico.usuario_pppoe ? ` (${servico.usuario_pppoe})` : '';
    return `${cliente} — ${contrato}${pppoe}`;
  }, []);

  return {
    dispositivos,
    loading,
    search,
    setSearch,
    filtroTipo,
    setFiltroTipo,
    stats,
    filteredDispositivos,
    activeStatusFilter,
    handleFilterChange,
    loadDispositivos,
    handleOpenCreate,
    handleOpenEdit,
    formOpen,
    setFormOpen,
    formData,
    setFormData,
    editing,
    saving,
    handleSave,
    deleteTarget,
    setDeleteTarget,
    handleDelete,
    servicoOptions,
    servicoSearchLoading,
    selectedServico,
    setSelectedServico,
    handleServicoSearch,
    getServicoLabel,
  };
}

export type UseDispositivosRedeReturn = ReturnType<typeof useDispositivosRede>;
